/**
 * Entity Registry
 * 
 * Central registry for all game entities. Provides fast lookup by ID
 * and by physics body ID, eliminating the need for naming conventions.
 * 
 * This registry combines the original tag-based entity system with
 * the new type-safe EntityInfo system for collision handling.
 */

import { AbstractMesh } from '@babylonjs/core';
import { logger } from './Logger';
import { BehaviorRegistry, type EntityBehavior } from './BehaviorRegistry';
import { 
  EntityType, 
  CharacterSubtype,
  type EntityInfo 
} from '../types/EntityTypes';
import type { AnyActorRef } from 'xstate';

// ============================================
// LEGACY ENTITY INTERFACE (backward compat)
// ============================================

export interface Entity {
  id: string;
  tags: string[];
  mesh?: AbstractMesh;
  update?(deltaTime: number): void;
  destroy?(): void;
}

// ============================================
// NEW TYPE-SAFE ENTITY OPTIONS
// ============================================

/**
 * Options for registering an entity with the type-safe system
 */
export interface RegisterEntityInfoOptions {
  /** Unique entity ID */
  id: string;
  
  /** Physics body ID (defaults to entity ID if not provided) */
  physicsBodyId?: string;
  
  /** Entity type */
  type: EntityType;
  
  /** Behavior ID for BehaviorRegistry lookup */
  behaviorId?: string;
  
  /** Character type for characters */
  characterType?: string;
  
  /** Character subtype */
  characterSubtype?: CharacterSubtype;
  
  /** Whether this is the player */
  isPlayer?: boolean;
  
  /** XState actor */
  actor?: AnyActorRef;
  
  /** Primary mesh */
  mesh?: AbstractMesh;
  
  /** Display name for debugging */
  displayName?: string;
  
  /** Custom metadata */
  metadata?: Record<string, unknown>;
}

// ============================================
// ENTITY REGISTRY CLASS
// ============================================

export class EntityRegistry {
  // --- Legacy entity storage ---
  private entities: Map<string, Entity> = new Map();
  
  // --- New type-safe entity storage ---
  private entityInfos = new Map<string, EntityInfo>();
  private physicsBodyToEntity = new Map<string, string>();
  private currentPlayerId: string | null = null;

  // ============================================
  // LEGACY METHODS (backward compatible)
  // ============================================

  register(entity: Entity): void {
    if (this.entities.has(entity.id)) {
      logger.core.warn(`Entity with id "${entity.id}" already registered`);
      return;
    }
    this.entities.set(entity.id, entity);
  }

  unregister(id: string): void {
    const entity = this.entities.get(id);
    if (entity?.destroy) {
      entity.destroy();
    }
    this.entities.delete(id);
    
    // Also unregister from new system
    this.unregisterEntityInfo(id);
  }

  get(id: string): Entity | undefined {
    return this.entities.get(id);
  }

  getByTag(tag: string): Entity[] {
    return Array.from(this.entities.values()).filter(entity => 
      entity.tags.includes(tag)
    );
  }

  update(deltaTime: number): void {
    for (const entity of this.entities.values()) {
      if (entity.update) {
        entity.update(deltaTime);
      }
    }
  }

  clear(): void {
    for (const [_id, entity] of this.entities.entries()) {
      if (entity.destroy) {
        entity.destroy();
      }
    }
    this.entities.clear();
    
    // Also clear new system
    this.entityInfos.clear();
    this.physicsBodyToEntity.clear();
    this.currentPlayerId = null;
    logger.core.info('Entity registry cleared');
  }

  getAll(): Entity[] {
    return Array.from(this.entities.values());
  }

  // ============================================
  // NEW TYPE-SAFE METHODS
  // ============================================

  /**
   * Register an entity with the type-safe system
   */
  registerEntityInfo(options: RegisterEntityInfoOptions): EntityInfo {
    const physicsBodyId = options.physicsBodyId ?? options.id;
    // behaviorId is optional - static geometry may not have a behavior
    const behaviorId = options.behaviorId;
    
    const info: EntityInfo = {
      id: options.id,
      physicsBodyId,
      type: options.type,
      behaviorId,
      characterType: options.characterType,
      characterSubtype: options.characterSubtype,
      isPlayer: options.isPlayer ?? false,
      actor: options.actor,
      mesh: options.mesh,
      displayName: options.displayName ?? options.id,
      metadata: options.metadata ?? {},
    };
    
    // Store in type-safe map
    this.entityInfos.set(options.id, info);
    
    // Store physics body mapping
    this.physicsBodyToEntity.set(physicsBodyId, options.id);
    
    // Track player
    if (options.isPlayer) {
      this.currentPlayerId = options.id;
    }
    
    logger.core.debug(`Registered entity: ${options.id} (type=${options.type}, behavior=${info.behaviorId})`);
    
    return info;
  }

  /**
   * Get the behavior for an entity
   */
  getBehavior(entityId: string): EntityBehavior | undefined {
    const info = this.entityInfos.get(entityId);
    if (!info?.behaviorId) return undefined;
    return BehaviorRegistry.getDirect(info.behaviorId);
  }

  /**
   * Unregister an entity from the type-safe system
   */
  unregisterEntityInfo(entityId: string): void {
    const info = this.entityInfos.get(entityId);
    if (!info) {
      return; // Not registered in new system, that's ok
    }
    
    // Remove from physics mapping
    this.physicsBodyToEntity.delete(info.physicsBodyId);
    
    // Remove from main map
    this.entityInfos.delete(entityId);
    
    // Clear player if this was the player
    if (info.isPlayer) {
      this.currentPlayerId = null;
    }
    
    logger.core.debug(`Unregistered entity info: ${entityId}`);
  }

  /**
   * Get entity info by entity ID
   */
  getEntityInfo(entityId: string): EntityInfo | undefined {
    return this.entityInfos.get(entityId);
  }

  /**
   * Get entity info by physics body ID (used in collision callbacks)
   */
  getEntityInfoByPhysicsId(physicsBodyId: string): EntityInfo | undefined {
    const entityId = this.physicsBodyToEntity.get(physicsBodyId);
    if (!entityId) return undefined;
    return this.entityInfos.get(entityId);
  }

  /**
   * Get entity info, returning a minimal "unknown" entity if not found
   */
  getEntityInfoOrUnknown(entityId: string): EntityInfo {
    const existing = this.entityInfos.get(entityId);
    if (existing) return existing;
    
    // Return minimal unknown entity
    return {
      id: entityId,
      physicsBodyId: entityId,
      type: EntityType.UNKNOWN,
      isPlayer: false,
      displayName: `Unknown (${entityId})`,
    };
  }

  /**
   * Get entity info by physics body ID, returning unknown if not found
   */
  getEntityInfoByPhysicsIdOrUnknown(physicsBodyId: string): EntityInfo {
    const existing = this.getEntityInfoByPhysicsId(physicsBodyId);
    if (existing) return existing;
    
    return {
      id: physicsBodyId,
      physicsBodyId,
      type: EntityType.UNKNOWN,
      isPlayer: false,
      displayName: `Unknown physics (${physicsBodyId})`,
    };
  }

  /**
   * Check if an entity is registered in the type-safe system
   */
  hasEntityInfo(entityId: string): boolean {
    return this.entityInfos.has(entityId);
  }

  /**
   * Check if a physics body is registered
   */
  hasPhysicsBody(physicsBodyId: string): boolean {
    return this.physicsBodyToEntity.has(physicsBodyId);
  }

  /**
   * Get the current player entity info
   */
  getPlayer(): EntityInfo | undefined {
    if (!this.currentPlayerId) return undefined;
    return this.entityInfos.get(this.currentPlayerId);
  }

  /**
   * Set the current player by entity ID.
   * This is called when player control switches between characters.
   * Updates both the currentPlayerId and the isPlayer flags on affected entities.
   */
  setCurrentPlayer(entityId: string | null): void {
    // Clear isPlayer flag on previous player
    if (this.currentPlayerId && this.currentPlayerId !== entityId) {
      const oldPlayer = this.entityInfos.get(this.currentPlayerId);
      if (oldPlayer) {
        oldPlayer.isPlayer = false;
      }
    }
    
    // Set new player
    this.currentPlayerId = entityId;
    
    // Set isPlayer flag on new player
    if (entityId) {
      const newPlayer = this.entityInfos.get(entityId);
      if (newPlayer) {
        newPlayer.isPlayer = true;
        logger.core.debug(`EntityRegistry: Current player set to ${entityId}`);
      } else {
        logger.core.warn(`EntityRegistry: setCurrentPlayer called with unknown entity: ${entityId}`);
      }
    } else {
      logger.core.debug('EntityRegistry: Current player cleared');
    }
  }

  /**
   * Get all entities of a specific type
   */
  getByType(type: EntityType): EntityInfo[] {
    const result: EntityInfo[] = [];
    for (const info of this.entityInfos.values()) {
      if (info.type === type) {
        result.push(info);
      }
    }
    return result;
  }

  /**
   * Get all registered entity infos
   */
  getAllEntityInfos(): EntityInfo[] {
    return Array.from(this.entityInfos.values());
  }

  /**
   * Get all characters (player and NPCs)
   */
  getCharacters(): EntityInfo[] {
    return this.getByType(EntityType.CHARACTER);
  }

  /**
   * Get all NPCs (non-player characters)
   */
  getNPCs(): EntityInfo[] {
    return this.getByType(EntityType.CHARACTER).filter(e => !e.isPlayer);
  }

  /**
   * Get all enemies
   */
  getEnemies(): EntityInfo[] {
    return this.getByType(EntityType.CHARACTER)
      .filter(e => e.characterSubtype === CharacterSubtype.ENEMY);
  }

  /**
   * Get all dynamic objects
   */
  getDynamicObjects(): EntityInfo[] {
    return this.getByType(EntityType.DYNAMIC_OBJECT);
  }

  /**
   * Get all entities with a specific behavior ID
   */
  getEntitiesByBehaviorId(behaviorId: string): EntityInfo[] {
    const result: EntityInfo[] = [];
    for (const info of this.entityInfos.values()) {
      if (info.behaviorId === behaviorId) {
        result.push(info);
      }
    }
    return result;
  }

  /**
   * Update an entity's actor reference
   */
  updateEntityActor(entityId: string, actor: AnyActorRef): void {
    const info = this.entityInfos.get(entityId);
    if (info) {
      info.actor = actor;
      logger.core.debug(`Updated actor for entity: ${entityId}`);
    } else {
      logger.core.warn(`Cannot update actor for unknown entity: ${entityId}`);
    }
  }

  /**
   * Update an entity's mesh reference
   */
  updateEntityMesh(entityId: string, mesh: AbstractMesh): void {
    const info = this.entityInfos.get(entityId);
    if (info) {
      info.mesh = mesh;
    }
  }

  /**
   * Find an entity by its associated mesh
   */
  getByMesh(mesh: AbstractMesh): EntityInfo | undefined {
    for (const info of this.entityInfos.values()) {
      if (info.mesh === mesh) {
        return info;
      }
    }
    return undefined;
  }

  /**
   * Update entity metadata
   */
  updateEntityMetadata(entityId: string, metadata: Record<string, unknown>): void {
    const info = this.entityInfos.get(entityId);
    if (info) {
      info.metadata = { ...info.metadata, ...metadata };
    }
  }

  /**
   * Get the count of type-safe registered entities
   */
  get entityInfoCount(): number {
    return this.entityInfos.size;
  }

  /**
   * Get statistics about registered entities
   */
  getStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const type of Object.values(EntityType)) {
      stats[type] = 0;
    }
    for (const info of this.entityInfos.values()) {
      stats[info.type]++;
    }
    return stats;
  }

  /**
   * Debug: dump all registered entities
   */
  debugDump(): void {
    logger.core.info('=== Entity Registry Dump ===');
    logger.core.info(`Legacy entities: ${this.entities.size}`);
    logger.core.info(`Type-safe entities: ${this.entityInfoCount}`);
    logger.core.info(`Stats: ${JSON.stringify(this.getStats())}`);
    for (const [id, info] of this.entityInfos) {
      logger.core.info(`  ${id}: type=${info.type}, physics=${info.physicsBodyId}, player=${info.isPlayer}`);
    }
  }
  
  // ============================================
  // SINGLETON ACCESSOR
  // ============================================
  
  private static _globalInstance: EntityRegistry | null = null;
  
  /**
   * Get or create the global EntityRegistry instance.
   * This provides a singleton accessor for systems that need registry access
   * without passing it through constructor chains.
   */
  static getInstance(): EntityRegistry {
    if (!EntityRegistry._globalInstance) {
      EntityRegistry._globalInstance = new EntityRegistry();
      logger.core.debug('EntityRegistry singleton created');
    }
    return EntityRegistry._globalInstance;
  }
  
  /**
   * Set the global instance (used by main.ts to register the manually-created instance)
   */
  static setInstance(instance: EntityRegistry): void {
    EntityRegistry._globalInstance = instance;
    logger.core.debug('EntityRegistry singleton set');
  }
}
